import React, { useId, useMemo, useCallback } from 'react'
import {
  Controller,
  useFieldArray,
  UseFieldArrayRemove,
  useFormContext,
} from 'react-hook-form'
import { FiltersSchema } from '../filtersSchema'
import {
  AsyncSelect,
  Box,
  Button,
  Field,
  Flex,
  useAppTheme,
} from '@workwhile/ui'
import { CircleMinus } from 'lucide-react'
import { useShiftsContextValue } from 'pages/Shifts/Shifts/context/ShiftsProvider'
import { indexBy, prop } from 'ramda'
import { NotSetAccountOrSalesOwnerFilterId } from 'api/shifts/getAllShifts'
import { FullOptions, MatchData, Searcher } from 'fast-fuzzy'

const NotSetSalesOwnerOption = {
  label: 'Not set',
  value: NotSetAccountOrSalesOwnerFilterId,
}

const AddedSalesOwnerRow = ({
  remove,
  index,
  value,
  isLast,
}: {
  remove: UseFieldArrayRemove
  index: number
  value: string
  isLast: boolean
}) => {
  const { colors, radii, space } = useAppTheme()
  return (
    <Flex
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      gap={space[2]}
      mb={isLast ? 0 : space[2]}
    >
      <Flex
        alignItems="flex-start"
        justifyContent="space-between"
        border="1px solid"
        borderColor={colors.neutrals[100]}
        p={2}
        borderRadius={radii.small}
        width="100%"
        position="relative"
      >
        {value}
      </Flex>
      <Button
        onClick={() => remove(index)}
        variant="text"
        color="red"
        size="small"
        danger
        style={{
          margin: 0,
          height: '1.6rem',
        }}
      >
        <CircleMinus size={16} />
      </Button>
    </Flex>
  )
}

const AddedSalesOwners = () => {
  const baseId = useId()
  const { watch, control } = useFormContext<FiltersSchema>()
  const {
    state: { salesOwners },
  } = useShiftsContextValue()

  const { remove } = useFieldArray({
    name: 'company.salesOwnerIds',
    control,
  })

  const salesOwnerIds = watch('company.salesOwnerIds') ?? []

  const salesOwnerMap = useMemo(() => {
    if (!salesOwners) return {}
    const groupedSalesOwners = indexBy(prop('id'), salesOwners)
    return groupedSalesOwners
  }, [salesOwners])

  const addedSalesOwnerRows = salesOwnerIds.map(({ id }, index) => (
    <AddedSalesOwnerRow
      key={`added-sales-owner-${baseId}-${id}-${index}`}
      remove={remove}
      index={index}
      value={
        id === NotSetAccountOrSalesOwnerFilterId
          ? 'No Sales Owner'
          : `${salesOwnerMap[id]?.name} (${id})`
      }
      isLast={index === salesOwnerIds.length - 1}
    />
  ))
  return (
    <Box width="256px" maxHeight="360px" overflow="auto">
      {addedSalesOwnerRows}
    </Box>
  )
}

type SalesOwnerOption = {
  label: string
  value: number | typeof NotSetAccountOrSalesOwnerFilterId
}

const fuzzyOptions: FullOptions<SalesOwnerOption> = {
  threshold: 0.7,
  ignoreCase: true,
  ignoreSymbols: true,
  normalizeWhitespace: true,
  returnMatchData: false,
  keySelector: (obj) => [obj.label, obj.value.toString()],
}

export const SalesOwnersFilter = () => {
  const { control, trigger, getValues, setValue, resetField } =
    useFormContext<FiltersSchema>()
  const {
    state: { salesOwners },
  } = useShiftsContextValue()

  const salesOwnerOptions = useMemo(() => {
    const options =
      salesOwners.map((salesOwner) => ({
        label: `${salesOwner.name} (${salesOwner.id})`,
        value: salesOwner.id,
      })) || []

    return [NotSetSalesOwnerOption, ...options]
  }, [salesOwners])

  const searcher = useMemo(() => {
    return new Searcher<SalesOwnerOption, FullOptions<SalesOwnerOption>>(
      salesOwnerOptions ?? [],
      fuzzyOptions
    )
  }, [salesOwnerOptions])

  const loadOptions = useCallback(
    (rawInput: string) => {
      if (!rawInput) return true

      const results = searcher.search(
        rawInput,
        fuzzyOptions
      ) as unknown as MatchData<SalesOwnerOption>[]

      return new Promise((resolve) => {
        resolve(results)
      })
    },
    [searcher]
  )

  const { append } = useFieldArray({
    name: 'company.salesOwnerIds',
    control,
  })

  return (
    <Box>
      <Controller
        control={control}
        name="company.salesOwnerIdUserInput"
        defaultValue={undefined}
        render={({ field, fieldState: { error, invalid } }) => (
          <Field error={error?.message?.toString()}>
            <Flex gap={2} width="256px" maxHeight="360px">
              <AsyncSelect
                block
                {...field}
                isClearable
                isSearchable
                defaultOptions={salesOwnerOptions}
                loadOptions={loadOptions}
                value={
                  salesOwnerOptions.find(
                    (option) => option.value === field.value
                  ) || null
                }
                onChange={(option) => {
                  field.onChange(option?.value)
                }}
                styles={{
                  container: (base) => ({
                    ...base,
                    width: '100%',
                  }),
                }}
              />
              <Button
                variant="primary"
                disabled={invalid}
                onClick={async () => {
                  const isInputValid = await trigger(
                    'company.salesOwnerIdUserInput'
                  )
                  const salesOwnerId = getValues(
                    'company.salesOwnerIdUserInput'
                  )
                  const addedSalesOwners =
                    getValues('company.salesOwnerIds') ?? []
                  const doesInputExistInCurrentGroup = addedSalesOwners.some(
                    (salesOwner) => salesOwner.id === salesOwnerId
                  )
                  const isValidNumber = !Number.isNaN(salesOwnerId)
                  const isNotSetSalesOwner =
                    salesOwnerId === NotSetAccountOrSalesOwnerFilterId

                  const isValid =
                    isInputValid &&
                    !doesInputExistInCurrentGroup &&
                    (isNotSetSalesOwner || isValidNumber) &&
                    typeof salesOwnerId !== 'undefined'

                  if (isValid) {
                    append({ id: salesOwnerId })
                    resetField('company.salesOwnerIdUserInput', {
                      defaultValue: undefined,
                    })
                    setValue('company.salesOwnerIdUserInput', undefined)
                  }

                  if (doesInputExistInCurrentGroup) {
                    resetField('company.salesOwnerIdUserInput', {
                      defaultValue: undefined,
                    })
                  }
                }}
              >
                Add
              </Button>
            </Flex>
          </Field>
        )}
      />

      <AddedSalesOwners />
    </Box>
  )
}
